import dynamic from 'next/dynamic';
import { lazily } from 'react-lazily';
import { useRouter } from 'next/router';
import { isPossiblePhoneNumber, Value } from 'react-phone-number-input';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useState, Suspense, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryCode } from 'libphonenumber-js';

import {
    Country,
    apiErrorHandler,
    getYearsOptions,
    validatePostcode,
    hasNestedProperty,
    postcodeSampleMap,
    getLocaleMonthOptions,
    getCountriesNormalize,
    getDayOptionsForMonthYear,
} from '@login/helpers';

import { calculateAgeByDate } from 'helpers/date';

import {
    GetRequiredInfoResponse,
    postRequiredInfo,
    PostRequiredInfoRequest,
    PostFullRegisterErrors,
    PostErrorFullRegisterResponse,
} from 'services/idm';

import { FormHeader } from '@login/components/form-header';
import { ApiErrorBlock } from '@login/components/api-error-block';
import { BirthPlaceBlock } from '@login/components/birth-place-block';

import styles from './required-info-form.module.scss';
import { FormBox } from '@login/components/form-box';
import { useScrollTo } from 'hooks/use-scroll-to';
import { useAppTranslation } from 'hooks/use-translates';
import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { EMAIL_PATTERN } from '@login/lib/constants';

const Button = dynamic(() => import('core/generic/elements/button'), {
    ssr: false,
});
const Loading = dynamic(() => import('core/generic/elements/loading'), {
    ssr: false,
});
const InfoBlock = dynamic(() => import('core/generic/elements/info-block'), {
    ssr: false,
});
const { Insert, Select, Phone, Hint } = lazily(
    () => import('core/generic/elements/form')
);
const { Divider } = lazily(() => import('core/generic/elements/divider'));

type RequiredInfoFormUrls = {
    registerUrl?: string;
    signInUrl?: string;
    forgottenPasswordUrl?: string;
    privacyPolicyUrl?: string;
    supportName?: string;
    siteName?: string;
    minimumAge?: string;
};

type RequiredInfoFormProps = {
    urls: RequiredInfoFormUrls | null;
    prefilledData: GetRequiredInfoResponse["data"];
    token?: string | null;
    countries: Country[] | null;
    channelSpecificFields: {
        showCountyOfBirthForSpecifiedCountries: string[];
        showAndRequireCityOfBirth?: boolean | undefined;
        showAndRequireCountryOfBirth?: boolean | undefined;
        showAndRequireCountyOfBirth?: boolean | undefined;
    } | undefined;
};

type FormDataType = {
    dayOfBirth: string;
    monthOfBirth: string;
    yearOfBirth: string;
    email: string;
    firstName: string;
    lastName: string;
    address: {
        countryCode: string;
        city: string;
        state?: string;
        addressLine1: string;
        addressLine2?: string;
        addressLine3?: string;
        postcode: string;
        birthCountry?: string;
        birthCounty?: string;
        birthCity?: string;
    };
    controls: {
        countryOfBirthEnabled?: boolean,
        countyOfBirthEnabled?: boolean,
        cityOfBirthEnabled?: boolean,
        dayOfBirthEnabled?: boolean,
        emailEnabled?: boolean,
        firstNameEnabled?: boolean,
        lastNameEnabled?: boolean,
        monthOfBirthEnabled?: boolean,
        yearOfBirthEnabled?: boolean,
    },
    mobilePhone: string;
};

export const RequiredInfoForm = ({
    urls,
    token,
    countries,
    prefilledData,
    channelSpecificFields,
}: RequiredInfoFormProps) => {
    const { i18n } = useTranslation();
    const { push } = useRouter();
    const { scrollToSection } = useScrollTo();
    const [apiErrorMessage, setApiErrorMessage] = useState<
        PostFullRegisterErrors | string | null
    >(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { t } = useAppTranslation(urls);
    const returnUrl = useCaseInsensitiveParam('returnUrl');
    const methods = useForm<PostRequiredInfoRequest>({ defaultValues: prefilledData, mode: 'onBlur' })
    const {
        control,
        handleSubmit,
        register,
        trigger,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = methods;

    const selectedMonth = useWatch({ control, name: 'monthOfBirth' });
    const selectedYear = useWatch({ control, name: 'yearOfBirth' });
    const selectedDay = useWatch({ control, name: 'dayOfBirth' });
    const selectedCountry = useWatch({ control, name: 'address.countryCode' });
    const selectedPostcode = useWatch({ control, name: 'address.postcode' });

    const dayOptions = useMemo(
        () => getDayOptionsForMonthYear(selectedMonth, selectedYear),
        [selectedMonth, selectedYear]
    );

    const defaultCountryCode: CountryCode = useMemo(() => {
        if (countries && countries.length && selectedCountry) {
            return countries.filter((value) => value.shortName === selectedCountry)[0].shortName as CountryCode;
        }
        return "IT"
    }, [selectedCountry]);

    const postcodeSample = useMemo(() => postcodeSampleMap[selectedCountry as CountryCode] || '', [selectedCountry]);

    const defaultValueBirth = useMemo(() => ({
        birthCountry: prefilledData?.address?.birthCountry || '',
        birthCounty: prefilledData?.address?.birthCounty || '',
        birthCity: prefilledData?.address?.birthCity || '',
    }), [prefilledData]);

    const birthPlaceControls = useMemo(() => ({
        countryOfBirthEnabled: prefilledData?.controls?.countryOfBirthEnabled,
        countyOfBirthEnabled: prefilledData?.controls?.countyOfBirthEnabled,
        cityOfBirthEnabled: prefilledData?.controls?.cityOfBirthEnabled,
    }), [prefilledData]);

    const isDOBDisabled = useMemo(() => (!prefilledData.controls?.dayOfBirthEnabled && !prefilledData.controls?.monthOfBirthEnabled && !prefilledData.controls?.yearOfBirthEnabled), [prefilledData.controls]);

    const hintTextDOB = useMemo(() => {
        if (isDOBDisabled) return t('requiredinfo.dob_help_disabled_info');
        return t('requiredinfo.dob_help_info');
    }, [isDOBDisabled]);

    useEffect(() => {
        const maxDay = dayOptions.length;
        if (+selectedDay > maxDay) {
            setValue('dayOfBirth', '1');
        }
        if (selectedDay && selectedMonth && selectedYear && urls?.minimumAge) {
            underMinAgeHandler(
                selectedDay,
                selectedMonth,
                selectedYear,
                urls.minimumAge
            );
        }
    }, [selectedMonth, selectedYear, selectedDay, dayOptions.length, setValue, urls?.minimumAge]);

    useEffect(() => {
        if (selectedPostcode) {
            trigger("address.postcode");
        }
    }, [selectedCountry, trigger]);

    const underMinAgeHandler = (
        day: string,
        month: string,
        year: string,
        minAge: string
    ) => {
        const birthDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
        );
        if (isNaN(birthDate.getTime())) {
            setError('birthDate', { message: 'Invalid birth date' });
            return;
        }

        const age = calculateAgeByDate(birthDate);

        if (age < Number(minAge)) {
            setError('birthDate', {
                message: t('requiredinfo.too_young_error_full')});
            return;
        } else clearErrors('birthDate');
    };

    const handleOnSubmit = async (formData: FormDataType) => {
        try {
            setApiErrorMessage(null);
            setIsSubmitting(true);
            const response = await postRequiredInfo(
                {
                    ...formData,
                    returnUrl
                },
                i18n.language,
                token
            );

            if (response.action === 'Redirect' && response.redirectUrl) {
                if (response.redirectUrl.startsWith('/connect')) {
                    const absoluteUrl = `${window.location.origin}${response.redirectUrl}`;
                    window.location.href = absoluteUrl;
                } else {
                    push(response.redirectUrl);
                }
            } else {
                setIsSubmitting(false);
            }
        } catch (e) {
            const error = e as PostErrorFullRegisterResponse;
            apiErrorHandler(error, i18n.language);
            if (error.title === 'VALIDATION_ERROR' && error.status === 400) {
                setApiErrorMessage(error.detail);
            } else if (
                error.title === 'VALIDATION_ERROR' &&
                error.status === 422
            ) {
                setApiErrorMessage(error.errors);
            } else if (error.title === 'USED_EMAIL_ERROR') {
                setApiErrorMessage({
                    usedEmail: t('requiredinfo.email_used_error_info'),
                });
            } else {
                setApiErrorMessage({
                    unknownServerError: t('common.500_error_message'),
                });
            }

            setIsSubmitting(false);
        }
    };

    const renderErrorBlock = useCallback(() => {
        let message = '';

        if (typeof apiErrorMessage === "string") {
            message = apiErrorMessage;
        } else if (apiErrorMessage?.usedEmail) {
            message = apiErrorMessage.usedEmail
                .replace("{signInUrl}", urls?.signInUrl || "")
                .replace("{forgottenPasswordUrl}", urls?.forgottenPasswordUrl || "");
        } else if (errors?.birthDate?.message) {
            message = errors.birthDate.message;
        } else if (apiErrorMessage?.unknownServerError) {
            message = apiErrorMessage.unknownServerError;
        }

        return message ? <ApiErrorBlock apiErrorMessage={message} /> : null;
    }, [errors, apiErrorMessage, urls]);

    useEffect(() => {
        const arrayOfKeys: string[] = [
            'birthDate',
            'usedEmail',
            'unknownServerError',
            'dayOfBirth',
            'monthOfBirth',
            'yearOfBirth',
            'email',
            'firstName',
            'lastName',
            'address.country',
            'address.addressLine1',
            'address.addressLine2',
            'address.addressLine3',
            'address.birthCountry',
            'address.birthCounty',
            'address.birthCity',
            'address.city',
            'address.postcode',
            'mobilePhone',
        ];
        let firstErrorElement = null;
        for (const key of arrayOfKeys) {
            if (apiErrorMessage?.hasOwnProperty(key)) {
                firstErrorElement = document.querySelector('[role="alert"]');
                break;
            }
            if (!key.includes('address') && errors?.hasOwnProperty(key)) {
                firstErrorElement = document.getElementById(`root-${key}`);
                break;
            }
            if (key.includes('address') && hasNestedProperty(errors, key)) {
                firstErrorElement = document.getElementById(`root-${key}`);
                break;
            }
            continue;
        }
        if (firstErrorElement && 'scrollIntoView' in firstErrorElement) {
            scrollToSection({ sectionRef: firstErrorElement, offset: 120 });
        }
    }, [errors, apiErrorMessage, scrollToSection]);

    return (
        <Suspense fallback={<Loading />}>
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(handleOnSubmit)}
                    id="form-profile"
                    noValidate
                >
                    <FormBox>
                        <FormHeader
                            headerTitle={t('common.register_tab')}
                        />
                        <div className={styles['form-box-content']}>
                            {renderErrorBlock()}
                            <div className={styles.registerDataSection}>
                                <div className={`body-large bold`}>{t('requiredinfo.required_info_title_info')}</div>
                                <div className='body-medium'>
                                    {t('requiredinfo.required_info_description_info')}
                                </div>
                            </div>
                            <InfoBlock variant="info-subtle">
                                <div className={styles['info-block']}>
                                    <span className="body-small">
                                        <span className={styles.redText}>*</span>
                                        {t('common.required_field_text')}
                                    </span>
                                </div>
                            </InfoBlock>
                            <div className={styles.dateSection}>
                                <div>
                                    <div
                                        className={`label-medium ${styles.labelPadding}`}
                                    >
                                        <p className={`${isDOBDisabled && styles.disabledText}`}>
                                            {t('requiredinfo.dob_label_info')}
                                        </p>
                                        <Hint text={hintTextDOB} />
                                    </div>
                                    <div className={styles.selectWrapper}>
                                        <Select
                                            {...register('dayOfBirth', {
                                                required: t(
                                                    'requiredinfo.day_required_error_info'
                                                ),
                                            })}
                                            name="dayOfBirth"
                                            trigger={trigger}
                                            control={control}
                                            id="dayOfBirth"
                                            label=""
                                            error={
                                                errors.dayOfBirth?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.DayOfBirth?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={!!prefilledData?.controls?.dayOfBirthEnabled}
                                            disabled={!prefilledData?.controls?.dayOfBirthEnabled}
                                            placeholder={t(
                                                'requiredinfo.day_placeholder_info'
                                            )}
                                            options={dayOptions}
                                        />
                                        <Select
                                            {...register('monthOfBirth', {
                                                required: t(
                                                    'requiredinfo.month_required_error_info'
                                                ),
                                            })}
                                            name="monthOfBirth"
                                            trigger={trigger}
                                            control={control}
                                            id="monthOfBirth"
                                            placeholder={t(
                                                'requiredinfo.month_placeholder_info'
                                            )}
                                            label=""
                                            error={
                                                errors.monthOfBirth?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.MonthOfBirth?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={!!prefilledData?.controls?.monthOfBirthEnabled}
                                            disabled={!prefilledData?.controls?.monthOfBirthEnabled}
                                            options={getLocaleMonthOptions(i18n.language)}
                                        />
                                        <Select
                                            {...register('yearOfBirth', {
                                                required: t(
                                                    'requiredinfo.year_required_error_info'
                                                ),
                                            })}
                                            name="yearOfBirth"
                                            trigger={trigger}
                                            control={control}
                                            id="yearOfBirth"
                                            placeholder={t(
                                                'requiredinfo.year_placeholder_info'
                                            )}
                                            label=""
                                            error={
                                                errors.yearOfBirth?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.YearOfBirth?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={!!prefilledData?.controls?.yearOfBirthEnabled}
                                            disabled={!prefilledData?.controls?.yearOfBirthEnabled}
                                            options={getYearsOptions()}
                                        />
                                    </div>
                                </div>
                            </div>
                            {!errors?.birthDate?.message && (
                                <>
                                    <div className={styles.registerDataSection}>
                                        <Insert
                                            {...register('firstName', {
                                                required: t('requiredinfo.first_name_required_error_info'),
                                            })}
                                            defaultValue={prefilledData?.firstName || ''}
                                            id="firstName"
                                            label={t('requiredinfo.first_name_label_info')}
                                            required={!!prefilledData?.controls?.firstNameEnabled}
                                            error={
                                                errors.firstName?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.FirstName?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={!prefilledData?.controls?.firstNameEnabled}
                                        />
                                        <Insert
                                            {...register('lastName', {
                                                required: t('requiredinfo.last_name_required_error_info'),
                                            })}
                                            defaultValue={prefilledData?.lastName || ''}
                                            id="lastName"
                                            label={t('requiredinfo.last_name_label_info')}
                                            required={!!prefilledData?.controls?.lastNameEnabled}
                                            error={
                                                errors.lastName?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.LastName?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={!prefilledData?.controls?.lastNameEnabled}
                                        />
                                    </div>
                                    <div className={styles.registerDataSection}>
                                        <Insert
                                            {...register('email', {
                                                required: t(
                                                    'requiredinfo.email_required_error_info'
                                                ),
                                                pattern: {
                                                    value: EMAIL_PATTERN,
                                                    message: t(
                                                        'requiredinfo.email_invalid_error_info'
                                                    ),
                                                },
                                            })}
                                            type='email'
                                            defaultValue={prefilledData?.email || ''}
                                            id="email"
                                            label={t('requiredinfo.email_label_info')}
                                            required={!!prefilledData?.controls?.emailEnabled}
                                            error={
                                                errors.email?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.Email?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={!prefilledData?.controls?.emailEnabled}
                                            hint={t("requiredinfo.email_hint_message")}
                                        />
                                    </div>
                                    <div className={styles.registerDataSection}>
                                        <Select
                                            {...register('address.countryCode', {
                                                required: t('requiredinfo.country_required_error_info'),
                                            })}
                                            name="address.countryCode"
                                            label={t('requiredinfo.country_label_info')}
                                            trigger={trigger}
                                            control={control}
                                            id="address.countryCode"
                                            placeholder={t('requiredinfo.country_placeholder_info')}
                                            error={
                                                errors.address?.countryCode?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.CountryCode']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={true}
                                            disabled={false}
                                            options={getCountriesNormalize(countries || [], "shortName", "IT")}
                                        />
                                        <Insert
                                            {...register('address.addressLine1', {
                                                required: t(
                                                    'requiredinfo.address_line1_required_error_info'
                                                ),
                                            })}
                                            defaultValue={prefilledData?.address?.addressLine1 || ''}
                                            id="address.addressLine1"
                                            label={t('requiredinfo.address_line1_label_info')}
                                            required={true}
                                            error={
                                                errors.address?.addressLine1?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.AddressLine1']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                        <Insert
                                            {...register('address.addressLine2')}
                                            defaultValue={prefilledData?.address?.addressLine2 || ''}
                                            id="address.addressLine2"
                                            label={t('requiredinfo.address_line2_label_info')}
                                            required={false}
                                            error={
                                                errors.address?.addressLine2?.message ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                            optional={true}
                                        />
                                        <Insert
                                            {...register('address.addressLine3')}
                                            defaultValue={prefilledData?.address?.addressLine3 || ''}
                                            id="address.addressLine3"
                                            label={t('requiredinfo.address_line3_label_info')}
                                            required={false}
                                            error={
                                                errors.address?.addressLine3?.message ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                            optional={true}
                                        />
                                        <Insert
                                            {...register('address.city', {
                                                required: t('requiredinfo.city_required_error_info'),
                                            })}
                                            defaultValue={prefilledData?.address?.city || ''}
                                            id="address.city"
                                            label={t('requiredinfo.city_label_info')}
                                            required={true}
                                            error={
                                                errors.address?.city?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.City']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                        <Insert
                                            {...register('address.postcode', {
                                                required: t('requiredinfo.postcode_required_error_info'),
                                                validate: (value) => {
                                                    if (!validatePostcode(selectedCountry as CountryCode, value)) {
                                                        return t('fullregister.postcode_invalid_error_full', {
                                                            postcodeSample,
                                                        });
                                                    }
                                                },
                                            })}
                                            defaultValue={prefilledData?.address?.postcode || ''}
                                            id="address.postcode"
                                            label={t('requiredinfo.postcode_label_info')}
                                            required={true}
                                            error={
                                                errors.address?.postcode?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.Postcode']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                    </div>
                                    <div className={styles.registerDataSection}>
                                        <Phone
                                            rules={{
                                                required: t('requiredinfo.mobile_phone_required_error_info'),
                                                validate: (value: string) => {
                                                    if (!isPossiblePhoneNumber(value)) {
                                                        return t('fullregister.mobile_phone_invalid_error_full');
                                                    }
                                                }
                                            }}
                                            label={t('requiredinfo.mobile_phone_label_info')}
                                            id="mobilePhone"
                                            name="mobilePhone"
                                            error={errors.mobilePhone?.message || (typeof apiErrorMessage !==
                                                'string' && apiErrorMessage &&
                                                apiErrorMessage?.MobilePhone?.join(
                                                    '<br>'
                                                )) || ''}
                                            errorIcon="AlertCircleFilled"
                                            defaultValue={
                                                prefilledData?.mobilePhone as Value
                                            }
                                            disabled={false}
                                            defaultCountry={defaultCountryCode}
                                            required={true}
                                            control={control}
                                            onChange={() => ''}
                                        />
                                    </div>
                                    <Divider />
                                    {(channelSpecificFields?.showAndRequireCityOfBirth || channelSpecificFields?.showAndRequireCountryOfBirth) && (
                                        <>
                                            <div className={styles.registerDataSection}>
                                                <BirthPlaceBlock
                                                    contriesWithCounty={channelSpecificFields.showCountyOfBirthForSpecifiedCountries}
                                                    countries={countries || []}
                                                    errors={errors}
                                                    channelSpecificFields={channelSpecificFields}
                                                    defaultValues={defaultValueBirth}
                                                    controls={birthPlaceControls}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <Button
                                        type="submit"
                                        label={t('requiredinfo.register_button_info')}
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        variant="primary"
                                        fullWidth={true}
                                        className={styles.submitButton}
                                        dimension="large"
                                    />
                                </>
                            )}
                        </div>
                    </FormBox>
                </form>
            </FormProvider>
        </Suspense>
    );
};

export default RequiredInfoForm;
